/**
 * Manual Trigger Panel Component
 * Dropdown-based job selector with context-sensitive inputs and actions
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Play, Loader2, CheckCircle, XCircle, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  triggerCustomerMetricsSync,
  triggerCustomerMetricsRefresh,
  triggerCustomerMetricsRefreshAsync,
  triggerAccountBaselineSync,
  triggerAccountBaselineRefresh,
  triggerDomainExclusionListCount,
  triggerDailyEventMetrics,
  triggerDailyEventMetricsBackfill,
  triggerCampaignReconProcedure,
  triggerCampaignReconSync,
  triggerLookupValuesSync,
} from '../queries/batchJobs'
import {
  triggerCampaignAttribution,
  triggerCampaignAttributionAll,
  triggerJourneyAttribution,
  triggerJourneyAttributionAll,
  triggerJourneyAttributionById,
} from '@/features/execution-monitoring/queries/attribution'

interface TriggerAction {
  label: string
  requiresAccountId?: boolean
  requiresDate?: boolean
  requiresFromDate?: boolean
  requiresJourneyId?: boolean
  fn: (
    accountId?: number,
    date?: string,
    entity?: string,
    field?: string,
    fromDate?: string,
    toDate?: string,
    journeyId?: string,
  ) => Promise<unknown>
}

interface JobConfig {
  value: string
  label: string
  description: string
  needsAccountId: boolean
  needsDate?: boolean
  needsEntity?: boolean
  needsField?: boolean
  needsFromDate?: boolean
  needsToDate?: boolean
  needsJourneyId?: boolean
  actions: TriggerAction[]
}

const JOB_CONFIGS: JobConfig[] = [
  {
    value: 'customer-metrics',
    label: 'Customer Metrics Sync',
    description: 'Sync or refresh customer metrics from Snowflake to MongoDB.',
    needsAccountId: true,
    actions: [
      { label: 'Incremental Sync', fn: (accId) => triggerCustomerMetricsSync(accId) },
      { label: 'Full Refresh', fn: (accId) => triggerCustomerMetricsRefresh(accId) },
      { label: 'Async Refresh', requiresAccountId: true, fn: (accId) => triggerCustomerMetricsRefreshAsync(accId!) },
    ],
  },
  {
    value: 'account-baseline',
    label: 'Account Baseline Sync',
    description: 'Sync or refresh account baseline data for one or all accounts.',
    needsAccountId: true,
    actions: [
      { label: 'Incremental Sync', fn: (accId) => triggerAccountBaselineSync(accId) },
      { label: 'Full Refresh', fn: (accId) => triggerAccountBaselineRefresh(accId) },
    ],
  },
  {
    value: 'domain-exclusion',
    label: 'Domain Exclusion List Count',
    description: 'Recount domain exclusion lists across all accounts.',
    needsAccountId: false,
    actions: [
      { label: 'Run Now', fn: () => triggerDomainExclusionListCount() },
    ],
  },
  {
    value: 'daily-event-metrics',
    label: 'Daily Event Metrics',
    description: "Refresh daily event metrics for yesterday across all accounts.",
    needsAccountId: false,
    actions: [
      { label: 'Run Now', fn: () => triggerDailyEventMetrics() },
    ],
  },
  {
    value: 'daily-event-metrics-backfill',
    label: 'Daily Event Metrics Backfill',
    description:
      'Backfill daily event metrics for a specific account over a date range. To-date is optional — leave blank to backfill from the from-date through today.',
    needsAccountId: true,
    needsFromDate: true,
    needsToDate: true,
    actions: [
      {
        label: 'Run Backfill',
        requiresAccountId: true,
        requiresFromDate: true,
        fn: (accId, _date, _entity, _field, fromDate, toDate) =>
          triggerDailyEventMetricsBackfill(accId!, fromDate!, toDate),
      },
    ],
  },
  {
    value: 'campaign-recon-procedure',
    label: 'Campaign Recon Procedure',
    description:
      'Run the CAMPAIGN_DAILY_RECON Snowflake procedure for a specific account and date (Step 1). After this completes, run Campaign Recon Sync to push results to MongoDB.',
    needsAccountId: true,
    needsDate: true,
    actions: [
      {
        label: 'Run Procedure',
        requiresAccountId: true,
        requiresDate: true,
        fn: (accId, date) => triggerCampaignReconProcedure(accId!, date!),
      },
    ],
  },
  {
    value: 'campaign-recon-sync',
    label: 'Campaign Recon Sync',
    description:
      'Sync campaign recon data from Snowflake into MongoDB for a specific account and date (Step 2). Run after Campaign Recon Procedure has completed.',
    needsAccountId: true,
    needsDate: true,
    actions: [
      {
        label: 'Run Sync',
        requiresAccountId: true,
        requiresDate: true,
        fn: (accId, date) => triggerCampaignReconSync(accId!, date!),
      },
    ],
  },
  {
    value: 'lookup-values-sync',
    label: 'Lookup Values Sync',
    description:
      'Fetch distinct values for fetch_value fields from the data warehouse and upsert them into lookup_master. All filters are optional.',
    needsAccountId: true,
    needsEntity: true,
    needsField: true,
    actions: [
      {
        label: 'Run Now',
        fn: (accId, _date, entity, field) => triggerLookupValuesSync(accId, entity, field),
      },
    ],
  },
  // ── Attribution ─────────────────────────────────────────────────────────────
  {
    value: 'campaign-attribution',
    label: 'Campaign Attribution',
    description:
      'Run campaign attribution for one account or all active accounts. Uses yesterday as the attribution date (same as the 2AM scheduled job). Synchronous — may take several minutes.',
    needsAccountId: true,
    actions: [
      {
        label: 'Run for Account',
        requiresAccountId: true,
        fn: (accId) => triggerCampaignAttribution(accId!),
      },
      {
        label: 'Run All Accounts',
        fn: () => triggerCampaignAttributionAll(),
      },
    ],
  },
  {
    value: 'journey-attribution',
    label: 'Journey Attribution',
    description:
      'Run journey attribution for one account or all active accounts. Optional date enables backfilling (safe to re-run — upsert semantics). Equivalent to the 3AM scheduled job.',
    needsAccountId: true,
    needsDate: true,
    actions: [
      {
        label: 'Run for Account',
        requiresAccountId: true,
        fn: (accId, date) => triggerJourneyAttribution(accId!, date),
      },
      {
        label: 'Run All Accounts',
        fn: () => triggerJourneyAttributionAll(),
      },
    ],
  },
  {
    value: 'journey-attribution-single',
    label: 'Journey Attribution (Single Journey)',
    description:
      'Run attribution for a specific journey by its MongoDB _id. Account is resolved from the journey itself. Useful for debugging or backfilling a single journey.',
    needsAccountId: false,
    needsJourneyId: true,
    needsDate: true,
    actions: [
      {
        label: 'Run Now',
        requiresJourneyId: true,
        fn: (_accId, date, _entity, _field, _fromDate, _toDate, journeyId) =>
          triggerJourneyAttributionById(journeyId!, date),
      },
    ],
  },
]

interface TriggerResult {
  success: boolean
  message: string
}

function getYesterday(): string {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toLocaleDateString('sv') // 'sv' locale gives ISO YYYY-MM-DD in local timezone
}

export function ManualTriggerPanel() {
  const [selectedJob, setSelectedJob] = useState<JobConfig>(JOB_CONFIGS[0])
  const [accountId, setAccountId] = useState<number | undefined>(undefined)
  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  const [result, setResult] = useState<TriggerResult | null>(null)
  const [date, setDate] = useState<string>(getYesterday())
  const [entity, setEntity] = useState<string>('')
  const [field, setField] = useState<string>('')
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [journeyId, setJourneyId] = useState<string>('')

  const mutation = useMutation({
    mutationFn: async ({ label, fn }: { label: string; fn: () => Promise<unknown> }) => {
      setActiveLabel(label)
      return fn()
    },
    onSuccess: (_, variables) => {
      setResult({ success: true, message: `"${variables.label}" triggered successfully.` })
      setActiveLabel(null)
    },
    onError: (err, variables) => {
      const message = err instanceof Error ? err.message : 'An error occurred'
      setResult({ success: false, message: `"${variables.label}" failed: ${message}` })
      setActiveLabel(null)
    },
  })

  const handleJobChange = (value: string) => {
    const job = JOB_CONFIGS.find(j => j.value === value)!
    setSelectedJob(job)
    setAccountId(undefined)
    setDate(getYesterday())
    setEntity('')
    setField('')
    setFromDate('')
    setToDate('')
    setJourneyId('')
    setResult(null)
  }

  const handleTrigger = (action: TriggerAction) => {
    if (action.requiresAccountId && accountId == null) {
      setResult({ success: false, message: `Account ID is required for "${action.label}".` })
      return
    }
    if (action.requiresDate && !date) {
      setResult({ success: false, message: `Date is required for "${action.label}".` })
      return
    }
    if (action.requiresFromDate && !fromDate) {
      setResult({ success: false, message: `From date is required for "${action.label}".` })
      return
    }
    if (action.requiresJourneyId && !journeyId.trim()) {
      setResult({ success: false, message: `Journey ID is required for "${action.label}".` })
      return
    }
    setResult(null)
    mutation.mutate({
      label: action.label,
      fn: () =>
        action.fn(
          accountId,
          date || undefined,
          entity || undefined,
          field || undefined,
          fromDate || undefined,
          toDate || undefined,
          journeyId.trim() || undefined,
        ),
    })
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">

      {/* Job selector */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Select Job</label>
        <div className="relative w-full max-w-sm">
          <select
            value={selectedJob.value}
            onChange={(e) => handleJobChange(e.target.value)}
            className="w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-8 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {JOB_CONFIGS.map(job => (
              <option key={job.value} value={job.value}>{job.label}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-gray-400" />
        </div>
        <p className="mt-1.5 text-xs text-gray-500">{selectedJob.description}</p>
      </div>

      {/* Account ID — only for jobs that need it */}
      {selectedJob.needsAccountId && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Account ID <span className="text-gray-400">(leave blank for all accounts)</span>
          </label>
          <input
            type="number"
            placeholder="e.g. 1234"
            value={accountId ?? ''}
            onChange={(e) => {
              const val = e.target.value
              setAccountId(val === '' ? undefined : parseInt(val, 10))
            }}
            className="w-48 rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      )}

      {/* Journey ID — only for single-journey attribution */}
      {selectedJob.needsJourneyId && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Journey ID <span className="text-gray-400">(MongoDB _id)</span>
          </label>
          <Input
            type="text"
            placeholder="e.g. 1234567890"
            value={journeyId}
            onChange={(e) => setJourneyId(e.target.value)}
            className="w-56"
          />
        </div>
      )}

      {/* Date — only for jobs that need it */}
      {selectedJob.needsDate && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Date <span className="text-gray-400">(defaults to yesterday)</span>
          </label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-40"
          />
        </div>
      )}

      {/* From Date — only for jobs that need it */}
      {selectedJob.needsFromDate && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            From Date <span className="text-gray-400">(required)</span>
          </label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-40"
          />
        </div>
      )}

      {/* To Date — only for jobs that need it */}
      {selectedJob.needsToDate && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            To Date <span className="text-gray-400">(optional, defaults to today)</span>
          </label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-40"
          />
        </div>
      )}

      {/* Entity — only for jobs that need it */}
      {selectedJob.needsEntity && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Entity <span className="text-gray-400">(optional, e.g. transaction_item)</span>
          </label>
          <Input
            type="text"
            placeholder="e.g. transaction_item"
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            className="w-56"
          />
        </div>
      )}

      {/* Field — only for jobs that need it */}
      {selectedJob.needsField && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Field <span className="text-gray-400">(optional, e.g. item_name)</span>
          </label>
          <Input
            type="text"
            placeholder="e.g. item_name"
            value={field}
            onChange={(e) => setField(e.target.value)}
            className="w-56"
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {selectedJob.actions.map((action) => {
          const isLoading = mutation.isPending && activeLabel === action.label
          return (
            <Button
              key={action.label}
              variant="outline"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => handleTrigger(action)}
              className={cn(
                'gap-1.5',
                (action.requiresAccountId && accountId == null) && 'opacity-50',
                (action.requiresDate && !date) && 'opacity-50',
                (action.requiresFromDate && !fromDate) && 'opacity-50',
                (action.requiresJourneyId && !journeyId.trim()) && 'opacity-50',
              )}
            >
              {isLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
              {action.label}
            </Button>
          )
        })}
      </div>

      {/* Result */}
      {result && (
        <div
          className={cn(
            'flex items-start gap-2 rounded-lg p-3 text-sm',
            result.success
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200',
          )}
        >
          {result.success ? (
            <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          )}
          {result.message}
        </div>
      )}
    </div>
  )
}
